import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import RichTextEditor from '../../components/editor/RichTextEditor';
import {
  POP_STATE,
  POP_STATE_OPTIONS,
  mergePopDetailsWithLanguages,
  stripPopDetails,
} from './popAnnouncementConstants';

const EMPTY_FORM = {
  state: POP_STATE.ON,
  details: [],
};

export default function PopAnnouncementEditModal({ open, initialData, languages, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }
    setError('');
    setForm({
      state: initialData?.state ?? POP_STATE.ON,
      details: mergePopDetailsWithLanguages(languages, initialData?.details || []),
    });
  }, [open, initialData, languages]);

  function updateDetail(index, key, value) {
    setForm((prev) => {
      const nextDetails = [...prev.details];
      nextDetails[index] = { ...nextDetails[index], [key]: value };
      return { ...prev, details: nextDetails };
    });
  }

  async function handleImageUpload(index, event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      const url = await adminApi.uploadOssImage(file);
      updateDetail(index, 'image', url);
    } catch (err) {
      setError(err.message || '圖片上傳失敗');
    } finally {
      event.target.value = '';
    }
  }

  function handleReset() {
    setError('');
    setForm({
      state: initialData?.state ?? POP_STATE.ON,
      details: mergePopDetailsWithLanguages(languages, initialData?.details || []),
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    const details = stripPopDetails(form.details).filter(
      (item) => item.title?.trim() || item.body?.trim() || item.image?.trim()
    );
    const payload = {
      state: Number(form.state),
      details,
    };
    setSubmitting(true);
    try {
      await adminApi.updatePop(payload);
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="confirm-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__header">
          <h3>修改</h3>
          <button type="button" className="btn btn-link" onClick={onClose}>
            ×
          </button>
        </div>

        <form className="modal-panel__body pop-announcement-edit-form" onSubmit={handleSubmit}>
          {error ? <p className="form-message form-message--error">{error}</p> : null}

          <div className="form-field">
            <span className="form-field__label">狀態</span>
            <div className="product-status-radios">
              {POP_STATE_OPTIONS.map((option) => (
                <label key={option.value} className="product-status-radios__item">
                  <input
                    type="radio"
                    name="popState"
                    checked={Number(form.state) === option.value}
                    onChange={() => setForm((p) => ({ ...p, state: option.value }))}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>

          {form.details.map((detail, index) => (
            <section key={detail.lang} className="pop-announcement-lang-section">
              <div className="form-field">
                <label htmlFor={`pop-title-${detail.lang}`}>
                  <span className="form-label-required">標題（{detail._label || detail.lang}）</span>
                </label>
                <input
                  id={`pop-title-${detail.lang}`}
                  type="text"
                  value={detail.title || ''}
                  onChange={(e) => updateDetail(index, 'title', e.target.value)}
                />
              </div>

              <div className="form-field">
                <span className="form-label">彈窗公告小圖（{detail._label || detail.lang}）</span>
                <div className="carousel-image-upload">
                  {detail.image ? (
                    <img src={resolveAdminMediaUrl(detail.image)} alt="" className="carousel-thumb-preview" />
                  ) : (
                    <div className="carousel-thumb-placeholder">未設定</div>
                  )}
                  <label className="btn btn-success">
                    上傳彈窗公告小圖（{detail._label || detail.lang}）
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => handleImageUpload(index, e)}
                    />
                  </label>
                  {detail.image ? (
                    <button
                      type="button"
                      className="btn btn-link btn-sm"
                      onClick={() => updateDetail(index, 'image', '')}
                    >
                      清除
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="form-field form-field--full">
                <span className="form-label">內容（{detail._label || detail.lang}）</span>
                <RichTextEditor
                  value={detail.body || ''}
                  onChange={(value) => updateDetail(index, 'body', value)}
                />
              </div>
            </section>
          ))}

          <div className="modal-panel__footer admin-modal__footer--center">
            <button type="submit" className="btn btn-success" disabled={submitting}>
              {submitting ? '提交中…' : '提交'}
            </button>
            <button type="button" className="btn btn-warning" onClick={handleReset} disabled={submitting}>
              重置
            </button>
            <button type="button" className="btn btn-danger" onClick={onClose} disabled={submitting}>
              關閉
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
